<template>
  <div class="form">
    <div class="form-title"><span class="swt"></span>{{ title }}</div>

    <!-- whole ⇄ partial -->
    <div class="field">
      <label>รูปแบบ</label>
      <div class="seg">
        <button
          type="button"
          :class="{ on: scope === 'whole' }"
          @click="setScope('whole')"
        >
          ทั้งวัน
        </button>
        <button
          type="button"
          :class="{ on: scope === 'partial' }"
          @click="setScope('partial')"
        >
          บางช่วง
        </button>
      </div>
    </div>

    <!-- time range (partial only) -->
    <div v-if="scope === 'partial'" class="field">
      <label>ช่วงเวลา</label>
      <div class="twinputs">
        <WhTimeField :value="from" @input="setTime('from', $event)" />
        <span class="arrow">–</span>
        <WhTimeField :value="to" @input="setTime('to', $event)" />
      </div>
      <div v-if="rangeError" class="field-err">
        เวลาเริ่มต้องอยู่ก่อนเวลาเลิก
      </div>
    </div>

    <!-- note -->
    <div class="field">
      <label>หมายเหตุ</label>
      <textarea
        v-model="note"
        class="input"
        maxlength="255"
        placeholder="เพิ่มหมายเหตุ (ถ้ามี)"
      ></textarea>
    </div>

    <div class="form-foot">
      <button class="qbtn" @click="$emit('cancel')">ยกเลิก</button>
      <button class="qbtn solid" :disabled="!canConfirm" @click="confirm">
        {{ confirmLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import WhTimeField from "./WhTimeField.vue";

// A minimal leave editor: choose whole-day or a time range, plus an optional
// note. Deliberately separate from ShiftForm — leave has no room/recurrence,
// and mixing "add hours" with "remove hours" in one form is confusing.
export default {
  name: "LeaveForm",
  components: { WhTimeField },
  props: {
    title: { type: String, default: "บันทึกวันลา" },
    confirmLabel: { type: String, default: "บันทึก" },
    defaultFrom: { type: String, default: "09:00" },
    defaultTo: { type: String, default: "17:00" },
    // Prefill for editing: { scope:'whole'|'partial', from, to, note }
    initial: { type: Object, default: null },
  },
  data() {
    const i = this.initial || {};
    return {
      scope: i.scope || "whole",
      from: i.from || this.defaultFrom,
      to: i.to || this.defaultTo,
      note: i.note || "",
    };
  },
  computed: {
    rangeError() {
      // Whole-day leave needs no range; partial requires start before end.
      // "HH:MM" zero-padded strings compare correctly as strings.
      if (this.scope !== "partial") return false;
      return !this.from || !this.to || this.from >= this.to;
    },
    canConfirm() {
      return !this.rangeError;
    },
  },
  methods: {
    setScope(s) {
      this.scope = s;
      this.emitPreview();
    },
    setTime(field, val) {
      this[field] = val;
      this.emitPreview();
    },
    emitPreview() {
      // leave = subtract a range; whole-day (null range) empties the day.
      this.$emit("preview-update", {
        type: "leave",
        scope: this.scope,
        from: this.scope === "partial" ? this.from : null,
        to: this.scope === "partial" ? this.to : null,
      });
    },
    confirm() {
      if (!this.canConfirm) return;
      this.$emit("confirm", {
        scope: this.scope,
        from: this.from,
        to: this.to,
        note: this.note,
      });
    },
  },
  mounted() {
    this.emitPreview();
  },
  beforeDestroy() {
    this.$emit("preview-update", null);
  },
};
</script>

<style scoped>
.form {
  border: 1px solid var(--line);
  background: var(--surface);
  border-radius: var(--r);
  padding: 13px;
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.form-title {
  font-size: 14.5px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 9px;
}
.form-title .swt {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--leave);
}
.field label {
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  display: block;
  margin-bottom: 5px;
}
.field-err {
  font-size: 11.5px;
  color: var(--leave);
  margin-top: 6px;
}
.twinputs {
  display: flex;
  align-items: center;
  gap: 8px;
}
.arrow {
  color: var(--faint);
}
.input {
  font-family: var(--wh-num);
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  width: 100%;
  border: 1px solid var(--line-strong);
  background: var(--surface);
  border-radius: var(--r-sm);
  padding: 8px 11px;
  outline: none;
  transition: 0.14s;
}
.input:focus {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px var(--brand-tint);
}
textarea.input {
  font-family: var(--wh-font);
  font-weight: 400;
  font-size: 13.5px;
  resize: none;
  min-height: 44px;
}
.seg {
  display: flex;
  background: var(--surface-3);
  border-radius: var(--r-sm);
  padding: 3px;
  gap: 3px;
}
.seg button {
  flex: 1;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  border: 0;
  background: transparent;
  color: var(--muted);
  padding: 7px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.12s;
}
.seg button.on {
  background: var(--surface);
  color: var(--brand);
  box-shadow: var(--shadow-card);
}
.form-foot {
  display: flex;
  gap: 9px;
}
.qbtn {
  flex: 1;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--line-strong);
  background: var(--surface);
  color: var(--ink-2);
  border-radius: var(--r-sm);
  padding: 9px 10px;
  transition: 0.14s;
}
.qbtn:hover {
  border-color: currentColor;
}
.qbtn.solid {
  background: var(--brand);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 6px 14px -6px color-mix(in srgb, var(--brand) 60%, transparent);
}
.qbtn.solid:hover {
  filter: brightness(1.04);
}
.qbtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
